import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type RefObject } from "react";
import { useSiteBase } from "@/lib/marketing/site-version-shared";

/** Whether the page shows its own link to `href` on screen, outside `bar`. The bar's copy of a
 * call to action steps aside while it does, so one screen never offers the same link twice. */
export function useShownElsewhere(href: string, bar: RefObject<HTMLElement | null>): boolean {
  const base = useSiteBase();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  /* Tagged with the page it was seen on, so a new page starts from "not shown". */
  const [seen, setSeen] = useState({ on: "", shown: false });

  useEffect(() => {
    if (typeof IntersectionObserver !== "function") return;
    const target = `${base}${href}`;
    const links = [...document.querySelectorAll<HTMLAnchorElement>("a[href]")].filter(
      (link) => link.pathname === target && !bar.current?.contains(link),
    );
    const inView = new Set<Element>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) inView.add(entry.target);
        else inView.delete(entry.target);
      }
      setSeen({ on: pathname, shown: inView.size > 0 });
    });
    for (const link of links) observer.observe(link);
    return () => observer.disconnect();
  }, [bar, base, href, pathname]);

  return seen.on === pathname && seen.shown;
}
